import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CustomModal, { CustomModalBasicProps, GridSection } from 'src/components/CustomModal';
import useObjectState from 'src/hooks/useObjectState';
import _ from 'lodash';
import fhirpath from 'fhirpath';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { capitalize, generateId, toPascalCase } from 'src/utils/string';
import { StructureDefinition } from 'src/careflow/ph-core/fhir-client/resources';
import { WrappedStructureDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/StructureDefinition';
import {
  ValueSetWrapper,
  WrappedValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { useValueSets } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { getOrganizationsByIds } from 'src/careflow/ph-core/admin/organization/service/organizations';

enum appliesTo {
  PATIENT = 'Patient',
}
const statusSelect: StructureDefinition['status'][] = ['draft', 'active', 'retired', 'unknown'];

export interface ErrorFieldState {
  template?: string | null;
  binding?: string | null;
  organization?: string | null;
  name?: string | null;
  description?: string | null;
  title?: string | null;
  status?: string | null;
}

export interface CustomFieldModalProps extends CustomModalBasicProps {
  systemTemplate: WrappedStructureDefinition[];
  rowToEdit: WrappedStructureDefinition | null;
  userOrganizationsIds: string[];
  configurations: any;
  filterVSByResource?: boolean;
  handlerCreate: Function;
}

interface CustomFieldState {
  organizationsList: any;
  template: WrappedStructureDefinition | null;
  binding: WrappedValueSet | null;
  organization: any | null;
  description: string | null;
  title: string | null;
  status: string | null;
}

export default function CustomFieldModal({
  onClose,
  systemTemplate,
  rowToEdit,
  userOrganizationsIds,
  configurations,
  filterVSByResource,
  handlerCreate,
  open,
  ...modalProps
}: CustomFieldModalProps) {
  const { i18n } = useLocales();
  const [valueSets, setValueSets] = useState<any>([]);

  const [
    { organizationsList, template, binding, organization, description, title, status },
    updateState,
  ] = useObjectState<CustomFieldState>({
    organizationsList: [],
    template: null,
    binding: null,
    organization: null,
    description: null,
    title: null,
    status: null,
  });

  const [errors, updateErrorState] = useObjectState<ErrorFieldState>({
    binding: null,
    description: null,
    title: null,
    organization: null,
    status: null,
  });

  const templateSelected = template ?? rowToEdit;

  const isCodeableConcept = useMemo(() => {
    if (!templateSelected) return false;

    return Boolean(
      fhirpath.evaluate(
        templateSelected,
        "differential.element.where(path = 'Extension.value[x]').type.code = 'CodeableConcept'"
      )?.[0]
    );
  }, [templateSelected]);

  const [, { find }] = useValueSets({
    map: ValueSetWrapper,
    autofetch: false,
  });

  const fetchValueSets = useCallback(async () => {
    const vs = await find({
      status: 'active',
      ...(filterVSByResource && { _tag: appliesTo.PATIENT }),
    });

    setValueSets(vs);
  }, [find, filterVSByResource]);

  useEffect(() => {
    if (!open || !isCodeableConcept) return;
    fetchValueSets();
  }, [open, isCodeableConcept, fetchValueSets]);

  const getBoundValueSet = (sd: any, valueSets?: any) => {
    const [canonical] =
      fhirpath.evaluate(
        sd,
        "differential.element.where(path = 'Extension.value[x]').binding.valueSet"
      ) ?? [];

    if (!canonical) return undefined;

    const id = getFhirIdFromEntity(canonical);
    return valueSets?.find((vs) => vs?.id === id);
  };

  const applyRowEditValues = useCallback(() => {
    if (!rowToEdit) return;

    const organizationRef = rowToEdit?.useContext?.[0]?.valueReference;

    updateState({
      description: rowToEdit.description,
      title: rowToEdit.title,
      status: rowToEdit.status,
      organization: {
        name: organizationRef?.display ?? '',
        fhirId: getFhirIdFromEntity(organizationRef?.reference ?? '') ?? '',
      },
      binding: getBoundValueSet(rowToEdit, valueSets),
    });
  }, [rowToEdit, valueSets]);

  const syncedBinding = useMemo(() => {
    if (!binding || !valueSets?.length) return null;
    return valueSets.find((vs) => vs.id === binding.id) ?? null;
  }, [binding, valueSets]);

  useEffect(() => {
    if (!open || !rowToEdit) return;
    applyRowEditValues();
  }, [open, rowToEdit, applyRowEditValues]);

  useEffect(() => {
    if (!open) return;

    const loadOrganizations = async () => {
      const organizations = await getOrganizationsByIds(userOrganizationsIds);

      updateState({
        organizationsList: organizations,
        ...(organizations?.length === 1 && {
          organization: organizations[0],
        }),
      });
    };

    loadOrganizations();
  }, [open, userOrganizationsIds]);

  const handlerClose = () => {
    updateState({
      organizationsList: [],
      template: null,
      binding: null,
      organization: null,
      title: null,
      description: null,
      status: null,
    });
    updateErrorState({
      template: null,
      binding: null,
      organization: null,
      title: null,
      description: null,
      status: null,
    });
    onClose?.({}, 'backdropClick');
  };

  const validateRequiredFields = () => {
    const errors: ErrorFieldState = {};
    if (!template && !rowToEdit)
      errors.template = i18n('customFields.modal.errors.template', 'crs');
    if (!binding && isCodeableConcept)
      errors.binding = i18n('customFields.modal.errors.binding', 'crs');
    if (!description) errors.description = i18n('customFields.modal.errors.description', 'crs');
    if (!title) errors.title = i18n('customFields.modal.errors.title', 'crs');
    if (!status) errors.status = i18n('customFields.modal.errors.status', 'crs');
    if (!organization?.fhirId)
      errors.organization = i18n('customFields.modal.errors.organization', 'crs');

    if (Object.keys(errors).length) {
      updateErrorState({ ...errors });
      return false;
    }
    return true;
  };

  const saveCustomField = async () => {
    if (!validateRequiredFields()) return;

    const { purpose, meta, ...clean } = template ?? {};

    const cleanName = toPascalCase(title ?? '');

    const elements = template?.differential?.element ?? rowToEdit?.differential?.element;

    const updateElements = elements?.map((el) => {
      if (el.path === 'Extension.value[x]' && el.type?.[0]?.code === 'CodeableConcept') {
        return {
          ...el,
          binding: {
            strength: 'required',
            valueSet: `${configurations?.baseUrl}/${binding?.resourceType}/${binding?.id}`,
          },
        };
      }

      return el;
    });

    const map = {
      ...clean,
      id: rowToEdit?.id ? rowToEdit?.id : generateId(title ?? ''),
      name: cleanName,
      description: description,
      title: title,
      date: moment().toISOString(),
      status: status,
      url: `${configurations?.baseUrl}/StructureDefinition/${cleanName}`,
      context: [
        {
          expression: appliesTo.PATIENT,
          type: 'element',
        },
      ],
      useContext: [
        {
          code: {
            system: `${configurations?.baseUrl}/CodeSystem/usage-context-type`,
            code: 'organization',
          },
          valueReference: {
            reference: `Organization/${organization?.fhirId}`,
            display: `${organization?.name}`,
          },
        },
      ],
      differential: {
        element: updateElements,
      },
    };
    handlerCreate(map);
    handlerClose();
  };

  const titleModal = rowToEdit?.id
    ? i18n('customFields.buttonTitleEdit', 'crs')
    : i18n('customFields.buttonTitleAdd', 'crs');

  return (
    <CustomModal
      {...modalProps}
      open={open}
      title={titleModal}
      breadcrumbs={[i18n('customFields.title', 'crs'), titleModal]}
      onSave={saveCustomField}
      onCancel={handlerClose as Function}
      onClose={handlerClose}
    >
      <Box sx={{ my: 3, width: '100%' }}>
        <Grid container sx={{ width: '100%' }} my={3}>
          <Grid item xs={12}>
            {!rowToEdit && (
              <GridSection mt={0}>
                <Autocomplete
                  fullWidth
                  disabled={systemTemplate?.length < 1}
                  options={systemTemplate}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={template}
                  onChange={(_: any, value: any | null) => {
                    updateErrorState({ template: null });
                    updateState({
                      template: value,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${i18n('customFields.modal.template', 'crs')}*`}
                      placeholder={`${i18n('customFields.modal.template', 'crs')}*`}
                      variant="outlined"
                      error={!!errors.template}
                      helperText={errors.template}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              </GridSection>
            )}
            {isCodeableConcept && (
              <GridSection>
                <Autocomplete
                  fullWidth
                  disabled={valueSets?.length < 1}
                  options={valueSets}
                  getOptionLabel={(option) => option.name ?? ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={syncedBinding}
                  onChange={(_: any, value: any | null) => {
                    updateErrorState({ binding: null });
                    updateState({
                      binding: value,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${i18n('customFields.modal.binding', 'crs')}*`}
                      placeholder={`${i18n('customFields.modal.binding', 'crs')}*`}
                      variant="outlined"
                      error={!!errors.binding}
                      helperText={errors.binding}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              </GridSection>
            )}
            <GridSection>
              <FormControl fullWidth>
                <InputLabel id="status-label">{`${i18n(
                  'customFields.modal.status',
                  'crs'
                )}*`}</InputLabel>
                <Select
                  id="status"
                  name="status"
                  label={`${i18n('customFields.modal.status', 'crs')}*`}
                  value={status}
                  fullWidth
                  error={!!errors.status}
                  placeholder={`${i18n('customFields.modal.status', 'crs')}*`}
                  onChange={(e) => {
                    updateState({ status: e.target.value });
                  }}
                >
                  {statusSelect.map((status) => (
                    <MenuItem key={status} value={status}>
                      {capitalize(status)}
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.status && <FormHelperText error>{errors.status}</FormHelperText>}
              </FormControl>
            </GridSection>
            <GridSection>
              <TextField
                fullWidth
                id="title"
                name="title"
                label={`${i18n('customFields.modal.title', 'crs')}*`}
                placeholder={`${i18n('customFields.modal.title', 'crs')}*`}
                error={!!errors.title}
                helperText={errors.title}
                variant="outlined"
                value={title}
                onChange={(e) => {
                  updateErrorState({ title: null });
                  updateState({ title: e.target.value });
                }}
              />
            </GridSection>
            <GridSection>
              <TextField
                fullWidth
                id="description"
                name="description"
                label={`${i18n('customFields.modal.description', 'crs')}*`}
                placeholder={`${i18n('customFields.modal.description', 'crs')}*`}
                error={!!errors.description}
                helperText={errors.description}
                variant="outlined"
                value={description}
                onChange={(e) => {
                  updateErrorState({ description: null });
                  updateState({ description: e.target.value });
                }}
              />
            </GridSection>
            <GridSection>
              <Autocomplete
                fullWidth
                disabled={organizationsList?.length < 1 || organizationsList?.length === 1}
                options={organizationsList}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.fhirId === value.fhirId}
                value={organization}
                onChange={(_: any, value: any | null) => {
                  updateErrorState({ organization: null });
                  updateState({
                    organization: value,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${i18n('customFields.modal.organization', 'crs')}*`}
                    placeholder={`${i18n('customFields.modal.organization', 'crs')}*`}
                    variant="outlined"
                    error={!!errors.organization}
                    helperText={errors.organization}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />
            </GridSection>
            <GridSection>
              <TextField
                fullWidth
                disabled={true}
                id="appliesTo"
                name="appliesTo"
                label={`${i18n('customFields.modal.appliesTo', 'crs')}*`}
                placeholder={`${i18n('customFields.modal.appliesTo', 'crs')}*`}
                variant="outlined"
                value={appliesTo.PATIENT}
              />
            </GridSection>
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
}
